import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, UserPlus, LogIn, PackageSearch, Wrench, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/pldfxqlf_logo.png";
const MASCOT_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/erel0x7n_buzon.png";

const navLinks = [
  { name: 'Inicio', href: '#inicio' },
  { name: 'Servicios', href: '#servicios' },
  { name: 'Nosotros', href: '#nosotros' },
  { name: 'FAQ', href: '#faq' },
  { name: 'Contacto', href: '#contacto' },
];

const toolsLinks = [
  {
    name: 'Registro cliente',
    description: 'Regístrate como nuevo cliente',
    href: 'https://www.grupotechnoservers.com/reg/registro.asp?mynumber=3',
    icon: UserPlus,
    color: '#F5A623',
  },
  {
    name: 'Iniciar sesión — Casillero',
    description: 'Accede a tu casillero',
    href: 'https://apptecnokargo.us',
    icon: LogIn,
    color: '#0066CC',
  },
  {
    name: 'Rastrear envío',
    description: 'Sigue el estado de tu paquete',
    href: 'https://www.grupotechnoservers.com/tracking/ras_paq.asp?mynumber=3',
    icon: PackageSearch,
    color: '#2DD4BF',
  },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [isMobileToolsOpen, setIsMobileToolsOpen] = useState(false);
  const toolsRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close tools dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setIsToolsOpen(false);
      }
    };
    if (isToolsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isToolsOpen]);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
    setIsMobileToolsOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-header shadow-lg' : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.a
            href="#inicio"
            onClick={(e) => { e.preventDefault(); scrollToSection('#inicio'); }}
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            data-testid="header-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Tecno Envios Express" 
              className="h-14 w-auto"
            />
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-7" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                className="nav-link text-slate-700 hover:text-[#0066CC] font-medium transition-colors"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}

            {/* Tools Dropdown */}
            <div className="relative" ref={toolsRef}>
              <button
                onClick={() => setIsToolsOpen(!isToolsOpen)}
                className="nav-link flex items-center gap-1 text-slate-700 hover:text-[#0066CC] font-medium transition-colors"
                data-testid="nav-herramientas"
              >
                <Wrench className="w-4 h-4" />
                Herramientas
                <ChevronDown className={`w-4 h-4 transition-transform ${isToolsOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {isToolsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden"
                    data-testid="tools-dropdown"
                  >
                    <div className="p-2">
                      {toolsLinks.map((tool) => (
                        <a
                          key={tool.name}
                          href={tool.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsToolsOpen(false)}
                          className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
                          data-testid={`tool-link-${tool.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${tool.color}1A` }}
                          >
                            <tool.icon className="w-5 h-5" style={{ color: tool.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 text-slate-900 font-semibold text-sm">
                              {tool.name}
                              <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">{tool.description}</div>
                          </div>
                        </a>
                      ))}
                    </div>
                    <div className="border-t border-slate-100 px-4 py-2.5 bg-slate-50 text-xs text-slate-500 flex items-center gap-1.5">
                      <Wrench className="w-3 h-3" />
                      Acceso a nuestras plataformas
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          {/* CTA Button with Mascot */}
          <div className="hidden lg:flex items-center space-x-3">
            <img 
              src={MASCOT_URL} 
              alt="Mascota" 
              className="h-10 w-10 object-contain"
            />
            <Button
              onClick={() => scrollToSection('#contacto')}
              className="bg-[#0066CC] hover:bg-[#0052a3] text-white rounded-full px-6 py-2 font-semibold shadow-lg hover:shadow-xl transition-all"
              data-testid="nav-cotizar"
            >
              Cotizar Envío
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-100 max-h-[calc(100vh-80px)] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-3">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                  className="block text-lg text-slate-700 hover:text-[#0066CC] font-medium py-2"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ))}

              {/* Mobile Tools Submenu */}
              <div className="border-t border-slate-100 pt-2">
                <button
                  onClick={() => setIsMobileToolsOpen(!isMobileToolsOpen)}
                  className="w-full flex items-center justify-between text-lg text-slate-700 hover:text-[#0066CC] font-medium py-2"
                  data-testid="mobile-nav-herramientas"
                >
                  <span className="flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    Herramientas
                  </span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isMobileToolsOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isMobileToolsOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-2 pt-2 space-y-2">
                        {toolsLinks.map((tool) => (
                          <a
                            key={tool.name}
                            href={tool.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                            data-testid={`mobile-tool-link-${tool.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          >
                            <div
                              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${tool.color}1A` }}
                            >
                              <tool.icon className="w-4 h-4" style={{ color: tool.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 text-slate-900 font-semibold text-sm">
                                {tool.name}
                                <ExternalLink className="w-3 h-3 text-slate-400" />
                              </div>
                              <div className="text-xs text-slate-500 mt-0.5">{tool.description}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Button
                onClick={() => scrollToSection('#contacto')}
                className="w-full bg-[#0066CC] hover:bg-[#0052a3] text-white rounded-full py-3 font-semibold mt-4"
                data-testid="mobile-nav-cotizar"
              >
                Cotizar Envío
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
